/**
 * Modal para parar timer com edição de horário de fim
 * Baseado no modelo SistemaOs-V2.html
 * 
 * IMPORTANTE: Este modal SEMPRE renderiza no document.body principal
 * usando React Portal, mesmo quando chamado de dentro do PIP.
 * Isso garante que o modal não fique limitado ao tamanho da janela PIP.
 * 
 * @author Equipe GestaoeCRM
 * @date 01/10/2025
 */

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Clock, X, Save, Play, Pause, Share2 } from 'lucide-react';
import { WorkSession, formatDuration } from '../services/workSessionApi';
import TimeDistributionModal from './TimeDistributionModal';

// ============================================================================
// INTERFACES
// ============================================================================

interface StopTimerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (endTime: string) => Promise<void>;
  onDistributeTime?: (distributions: any[]) => Promise<void>;
  session: WorkSession | null;
  loading?: boolean;
  /** Documento alvo para renderizar o modal (usado quando está no PIP) */
  targetDocument?: Document;
}

// ============================================================================
// COMPONENTE STOPTIMMERMODAL
// ============================================================================

/**
 * Modal para parar timer com controle de tempo
 * 
 * Funcionalidades:
 * - Exibe informações da sessão atual
 * - Permite editar horário de fim
 * - Validação de horário
 * - Botões de ação (Salvar, Limpar, Cancelar)
 * - Design baseado no SistemaOs-V2.html
 */
export const StopTimerModal: React.FC<StopTimerModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  onDistributeTime,
  session,
  loading = false,
  targetDocument,
}) => {
  const [endDateTime, setEndDateTime] = useState('');
  const [isValidTime, setIsValidTime] = useState(true);
  const [calculatedDuration, setCalculatedDuration] = useState(0);
  const [showDistributionQuestion, setShowDistributionQuestion] = useState(false);
  const [showDistributionModal, setShowDistributionModal] = useState(false);

  // DEBUG: Log inicial do componente
  // console.log('🚀 StopTimerModal: Componente renderizado', {
  // isOpen,
  // session: !!session,
  // onDistributeTime: !!onDistributeTime,
  // onDistributeTimeType: typeof onDistributeTime,
  // onDistributeTimeFunction: onDistributeTime?.toString().substring(0, 100),
  // showDistributionQuestion,
  // timestamp: new Date().toISOString()
  // });

  // DEBUG: Verificar se o código foi compilado
  // console.log('🔧 StopTimerModal: CÓDIGO ATUALIZADO - v0.33.53 - 22/10/2025 - 13:40');

  // Preencher SEMPRE com a hora ATUAL do navegador (não resumedAt/pausedAt)
  // Isso garante que o valor inicial sempre reflete quando o usuário clicou em "Parar"
  useEffect(() => {
    if (isOpen && session) {
      // SEMPRE usar horário atual DO NAVEGADOR
      // O usuário quer parar AGORA, não no momento da última pausa/retomada
      const defaultDateTime = new Date();
      
      // Formatar para datetime-local usando timezone LOCAL (não UTC)
      // Isso garante que o campo mostra a hora CERTA para o usuário
      const year = defaultDateTime.getFullYear();
      const month = String(defaultDateTime.getMonth() + 1).padStart(2, '0');
      const day = String(defaultDateTime.getDate()).padStart(2, '0');
      const hours = String(defaultDateTime.getHours()).padStart(2, '0');
      const minutes = String(defaultDateTime.getMinutes()).padStart(2, '0');
      
      const dateTimeString = `${year}-${month}-${day}T${hours}:${minutes}`;
      
      // console.log('🕐 StopTimer: Pré-preenchendo campo com hora ATUAL do navegador:', {
      // now: defaultDateTime.toISOString(),
      // dateTimeString,
      // timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      // resumedAt: session.resumedAt,
      // pausedAt: session.pausedAt
      // });
      
      setEndDateTime(dateTimeString);
      setIsValidTime(true);
    }
  }, [isOpen, session]);

  // Efeito separado para verificar distribuição de tempo
  useEffect(() => {
    // console.log('🔄 StopTimer: useEffect executado', {
    // isOpen,
    // session: !!session,
    // onDistributeTime: !!onDistributeTime,
    // timestamp: new Date().toISOString()
    // });
    
    if (isOpen && session) {
      // DEBUG: Verificar se onDistributeTime está disponível
      // console.log('🔍 StopTimer: Verificando onDistributeTime:', {
      // onDistributeTime: !!onDistributeTime,
      // type: typeof onDistributeTime,
      // isFunction: typeof onDistributeTime === 'function'
      // });
      
      // TEMPORÁRIO: Sempre mostrar pergunta de distribuição para debug
      // console.log('🔧 StopTimer: FORÇANDO showDistributionQuestion = true para debug');
      setShowDistributionQuestion(true);
      
      // Verificar se o estado foi atualizado
      setTimeout(() => {
        // console.log('⏰ StopTimer: Estado após 100ms:', {
        // showDistributionQuestion,
        // timestamp: new Date().toISOString()
        // });
      }, 100);
    }
  }, [isOpen, session, onDistributeTime]);

  // Validar data e horário de fim
  // USA SEMPRE A HORA DO NAVEGADOR para validação
  const validateTime = (dateTime: string) => {
    if (!dateTime) {
      setIsValidTime(false);
      return false;
    }

    const endTimeObj = new Date(dateTime);
    
    // Validar se é uma data válida
    if (isNaN(endTimeObj.getTime())) {
      setIsValidTime(false);
      return false;
    }

    // Validar se não é anterior ao horário de início
    if (session?.startTime) {
      const startTimeObj = new Date(session.startTime);
      
      // Log para debug
      // console.log('🔍 StopTimer: Validando horário de fim...', {
      // endTime: endTimeObj.toISOString(),
      // startTime: startTimeObj.toISOString(),
      // endTimeLocal: endTimeObj.toLocaleString('pt-BR'),
      // startTimeLocal: startTimeObj.toLocaleString('pt-BR'),
      // isBeforeStart: endTimeObj <= startTimeObj
      // });
      
      if (endTimeObj <= startTimeObj) {
        // console.error('❌ StopTimer: Horário de fim anterior ao início');
        setIsValidTime(false);
        return false;
      }
    }

    // Validar se não é MUITO no futuro (tolerância de 5 minutos para diferença servidor/cliente)
    const now = new Date();
    const fiveMinutesAhead = new Date(now.getTime() + (5 * 60 * 1000));
    
    if (endTimeObj > fiveMinutesAhead) {
      // console.error('❌ StopTimer: Horário de fim muito no futuro (>5min)');
      setIsValidTime(false);
      return false;
    }

    // console.log('✅ StopTimer: Horário válido');
    setIsValidTime(true);
    return true;
  };

  // Calcular duração em tempo real baseado no endDateTime escolhido
  // USA A MESMA LÓGICA do calculateRealTimeDuration() mas com endDateTime em vez de now
  useEffect(() => {
    if (!endDateTime || !session?.startTime) {
      // console.log('⏱️ StopTimer: Cálculo não executado - dados faltando', {
      // endDateTime,
      // startTime: session?.startTime
      // });
      setCalculatedDuration(0);
      return;
    }

    try {
      const endTimeObj = new Date(endDateTime);
      const startTimeObj = new Date(session.startTime);
      
      // console.log('⏱️ StopTimer: Calculando duração...', {
      // endDateTime,
      // endTimeObj: endTimeObj.toISOString(),
      // startTime: session.startTime,
      // startTimeObj: startTimeObj.toISOString(),
      // totalPausedTime: session.totalPausedTime,
      // isPaused: session.isPaused,
      // pausedAt: session.pausedAt
      // });
      
      // Validar se é uma data válida
      if (isNaN(endTimeObj.getTime()) || isNaN(startTimeObj.getTime())) {
        // console.error('❌ StopTimer: Data inválida detectada');
        setCalculatedDuration(0);
        return;
      }

      // Calcular tempo total decorrido desde início até endDateTime
      const totalMs = endTimeObj.getTime() - startTimeObj.getTime();
      const totalHours = totalMs / (1000 * 60 * 60);

      // Calcular tempo de pausa atual se estiver pausada
      let currentPauseTime = 0;
      if (session.isPaused && session.pausedAt) {
        const pausedAt = new Date(session.pausedAt);
        const pauseMs = endTimeObj.getTime() - pausedAt.getTime();
        currentPauseTime = Math.max(0, pauseMs / (1000 * 60 * 60));
      }

      // Tempo total pausado acumulado (pausas anteriores)
      const totalPausedHours = session.totalPausedTime || 0;
      
      // Duração real = tempo total - tempo pausado acumulado - pausa atual
      const workedHours = Math.max(0, totalHours - totalPausedHours - currentPauseTime);
      
      // console.log('✅ StopTimer: Duração calculada:', {
      // totalMs,
      // totalHours: totalHours.toFixed(4),
      // totalPausedHours: totalPausedHours.toFixed(4),
      // currentPauseTime: currentPauseTime.toFixed(4),
      // workedHours: workedHours.toFixed(4),
      // formula: `${totalHours.toFixed(2)}h (total) - ${totalPausedHours.toFixed(2)}h (pausado) - ${currentPauseTime.toFixed(2)}h (pausa atual) = ${workedHours.toFixed(2)}h`
      // });
      
      setCalculatedDuration(workedHours);
    } catch (error) {
      // console.error('❌ StopTimer: Erro ao calcular duração:', error);
      setCalculatedDuration(0);
    }
  }, [endDateTime, session]);

  // Handler para mudança de data/horário
  const handleTimeChange = (dateTime: string) => {
    setEndDateTime(dateTime);
    validateTime(dateTime);
  };

  // Handler para confirmar parada
  const handleConfirm = async () => {
    if (!validateTime(endDateTime)) {
      return;
    }

    // Se não há função de distribuição, salvar diretamente
    if (!onDistributeTime) {
      try {
        // Converter para ISO string completa para o backend
        const endTimeISO = new Date(endDateTime).toISOString();
        await onConfirm(endTimeISO);
        onClose();
      } catch (error) {
        // console.error('Erro ao parar timer:', error);
      }
    } else {
      // Mostrar pergunta sobre distribuição
      setShowDistributionQuestion(true);
    }
  };

  // Handler para escolher salvar diretamente
  const handleSaveDirectly = async () => {
    try {
      const endTimeISO = new Date(endDateTime).toISOString();
      await onConfirm(endTimeISO);
      onClose();
    } catch (error) {
      // console.error('Erro ao parar timer:', error);
    }
  };

  // Handler para escolher distribuir tempo
  const handleDistributeTime = () => {
    setShowDistributionModal(true);
  };

  // Handler para confirmar distribuição
  const handleConfirmDistribution = async (distributions: any[]) => {
    try {
      if (onDistributeTime) {
        await onDistributeTime(distributions);
        onClose();
      }
    } catch (error) {
      // console.error('Erro ao distribuir tempo:', error);
    }
  };

  // Handler para limpar formulário
  const handleClear = () => {
    setEndDateTime('');
    setIsValidTime(false);
  };

  // Handler para preencher horário atual DO NAVEGADOR (hora local)
  const handleFillCurrentTime = () => {
    const now = new Date();
    
    // Formatar usando timezone LOCAL do navegador
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    
    const dateTimeString = `${year}-${month}-${day}T${hours}:${minutes}`;
    
    // console.log('🕐 StopTimer: Preenchendo com hora ATUAL do navegador:', {
    // now: now.toISOString(),
    // dateTimeString,
    // timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    // });
    
    setEndDateTime(dateTimeString);
    validateTime(dateTimeString);
  };

  if (!isOpen || !session) {
    return null;
  }

  // SEMPRE renderizar no document.body principal usando Portal
  // Isso garante que o modal não fique preso dentro da janela PIP
  const modalContent = (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
      <div className="bg-background-glass backdrop-blur-glass border border-border-glass shadow-glass w-full max-w-lg sm:max-w-xl md:max-w-2xl relative flex flex-col overflow-hidden max-h-[100dvh] sm:max-h-[90vh] rounded-none sm:rounded-button">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary to-secondary text-white p-6 rounded-none sm:rounded-t-button flex-none">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Clock className="w-6 h-6" />
              <h3 className="text-xl font-semibold">Controle de Tempo</h3>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 flex-1 overflow-y-auto min-h-0">
          {/* Informações da Sessão */}
          <div className="mb-6">
            <h4 className="text-lg font-medium text-text-primary mb-2">
              Finalizar Sessão de Trabalho
            </h4>
            <p className="text-sm text-text-muted mb-4">
              {session.orderOfService?.title || 'Sessão sem título'}
            </p>
            
            {/* Informações de tempo - COM SCROLL para não crescer muito */}
            <div className="bg-background-glass-hover rounded-button p-4 border border-border-glass/30 max-h-[240px] overflow-y-auto custom-scrollbar-modal">
              <div className="space-y-2">
                {/* Timeline de Atividades */}
                <div className="space-y-2 pb-2 border-b border-border-glass/20">
                  <div className="flex justify-between text-sm items-center">
                    <span className="text-text-muted flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Início:
                    </span>
                    <span className="font-medium text-text-primary">
                      {new Date(session.startTime).toLocaleString('pt-BR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                  
                  {session.pausedAt && (
                    <div className="flex justify-between text-sm items-center">
                      <span className="text-yellow-400 flex items-center gap-1">
                        <Pause className="w-3 h-3" />
                        Última pausa:
                      </span>
                      <span className="font-medium text-yellow-400">
                        {new Date(session.pausedAt).toLocaleString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                  )}
                  
                  {session.resumedAt && (
                    <div className="flex justify-between text-sm items-center">
                      <span className="text-green-400 flex items-center gap-1">
                        <Play className="w-3 h-3" />
                        Última retomada:
                      </span>
                      <span className="font-medium text-green-400">
                        {new Date(session.resumedAt).toLocaleString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                  )}
                </div>

                {/* Resumo de Tempos - CÁLCULO EM TEMPO REAL */}
                <div className="space-y-2 pt-2">
                  {/* Tempo trabalhado - FORMATO DUPLO (HH:MM:SS + horas) */}
                  <div className="bg-green-500/10 rounded-lg p-3 border border-green-500/20">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-text-muted font-bold text-sm">💰 Tempo a Pagar:</span>
                      <span className="font-mono text-xl font-bold text-green-400">
                        {formatDuration(calculatedDuration)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-text-muted">Em horas decimais:</span>
                      <span className="font-mono font-medium text-green-400">
                        {calculatedDuration.toFixed(3)}h
                      </span>
                    </div>
                    <div className="text-xs text-text-muted italic mt-1 text-center">
                      ⚠️ Este é o valor que será pago ao analista
                    </div>
                  </div>
                  
                  {/* Tempo pausado */}
                  {session.totalPausedTime > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-text-muted">Tempo pausado:</span>
                      <span className="font-medium text-yellow-400">
                        {formatDuration(session.totalPausedTime)} ({session.totalPausedTime.toFixed(3)}h)
                      </span>
                    </div>
                  )}
                  
                  {/* Tempo total (trabalhado + pausado) */}
                  {calculatedDuration > 0 && (
                    <div className="flex justify-between text-xs pt-2 border-t border-border-glass/20">
                      <span className="text-text-muted italic">Tempo total decorrido:</span>
                      <span className="font-medium text-text-muted italic">
                        {formatDuration(calculatedDuration + (session.totalPausedTime || 0))} ({(calculatedDuration + (session.totalPausedTime || 0)).toFixed(3)}h)
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Formulário de Controle de Tempo */}
          <div className="space-y-4">
            <h5 className="text-md font-medium text-text-primary flex items-center space-x-2">
              <Clock className="w-4 h-4 text-secondary" />
              <span>Definir Horário de Fim</span>
            </h5>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-text-secondary mb-2">
                  Data e Horário de Fim *
                </label>
                <input
                  type="datetime-local"
                  value={endDateTime}
                  onChange={(e) => handleTimeChange(e.target.value)}
                  className={`
                    w-full px-3 py-2 border rounded-button shadow-glass focus:ring-2 focus:ring-secondary focus:border-secondary/50
                    bg-background-glass text-text-primary placeholder-text-muted
                    ${!isValidTime ? 'border-red-400 bg-red-900/20' : 'border-border-glass'}
                  `}
                />
                {!isValidTime && (
                  <p className="text-xs text-red-400 mt-1">
                    Data/horário inválido, anterior ao início ou no futuro
                  </p>
                )}
                <p className="text-xs text-text-muted mt-1">
                  💡 Pré-preenchido com a última atividade registrada (pausa/retomada)
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={handleFillCurrentTime}
                  className="px-3 py-2 bg-secondary/20 text-secondary rounded-button hover:bg-secondary/30 transition-colors text-sm font-medium border border-secondary/30"
                >
                  Usar Agora
                </button>
                <button
                  type="button"
                  onClick={handleClear}
                  className="px-3 py-2 bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400 rounded-button transition-colors text-sm font-medium border border-yellow-500/30"
                >
                  Limpar
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 bg-background-glass-hover border-t border-border-glass/30 rounded-none sm:rounded-b-button flex-none">
          {/* DEBUG: Log do estado */}
          // {console.log('🔍 StopTimer: Renderizando footer com showDistributionQuestion:', showDistributionQuestion, 'isOpen:', isOpen, 'session:', !!session)}
          {!showDistributionQuestion ? (
            <div className="flex justify-center space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-border-glass rounded-button shadow-glass text-sm font-bold text-text-secondary bg-background-glass backdrop-blur-glass hover:bg-background-glass-hover"
              >
                Cancelar
              </button>
              
              <button
                type="button"
                onClick={handleConfirm}
                disabled={!isValidTime || loading}
                className={`
                  flex items-center space-x-2 px-4 py-2 font-bold rounded-button transition-colors shadow-glass
                  ${isValidTime && !loading
                    ? 'bg-gradient-to-r from-green-500 to-green-600 hover:brightness-110 text-white'
                    : 'bg-background-glass-hover text-text-muted cursor-not-allowed border border-border-glass'
                  }
                `}
              >
                {loading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                <span>{loading ? 'Salvando...' : 'Salvar Atividade'}</span>
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Pergunta sobre distribuição */}
              <div className="text-center">
                <h4 className="text-lg font-medium text-text-primary mb-2">
                  Deseja registrar todo o tempo nesta OS?
                </h4>
                <p className="text-sm text-text-muted mb-4">
                  Você pode escolher salvar todo o tempo na OS atual ou distribuí-lo entre outras OS.
                </p>
              </div>

              {/* Botões de escolha */}
              <div className="flex justify-center space-x-4">
                <button
                  type="button"
                  onClick={handleSaveDirectly}
                  disabled={loading}
                  className="flex items-center space-x-2 px-6 py-3 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-button transition-colors shadow-glass disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="w-4 h-4" />
                  <span>Sim, salvar aqui</span>
                </button>

                <button
                  type="button"
                  onClick={handleDistributeTime}
                  disabled={loading}
                  className="flex items-center space-x-2 px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-button transition-colors shadow-glass disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Share2 className="w-4 h-4" />
                  <span>Não, distribuir</span>
                </button>
              </div>

              {/* Botão voltar */}
              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={() => setShowDistributionQuestion(false)}
                  className="px-4 py-2 text-sm text-text-muted hover:text-text-primary transition-colors"
                >
                  ← Voltar
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Custom scrollbar styles para área de informações */}
      <style>{`
        .custom-scrollbar-modal {
          scrollbar-width: thin;
          scrollbar-color: rgba(147, 51, 234, 0.5) rgba(255, 255, 255, 0.05);
        }
        .custom-scrollbar-modal::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar-modal::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 3px;
        }
        .custom-scrollbar-modal::-webkit-scrollbar-thumb {
          background: rgba(147, 51, 234, 0.5);
          border-radius: 3px;
        }
        .custom-scrollbar-modal::-webkit-scrollbar-thumb:hover {
          background: rgba(147, 51, 234, 0.7);
        }
      `}</style>
    </div>
  );

  // CORREÇÃO v0.28.9: Renderizar no documento correto (PIP ou principal)
  // Se targetDocument foi fornecido (estamos no PIP), usar ele
  // Caso contrário, usar document.body da página principal
  const portalTarget = targetDocument?.body || document.body;
  
  // console.log('📺 StopTimerModal: Renderizando modal em:', {
  // isPIPContext: !!targetDocument,
  // targetTitle: portalTarget.ownerDocument?.title || 'documento principal'
  // });
  
  return (
    <>
      {createPortal(modalContent, portalTarget)}
      
      {/* Modal de Distribuição de Tempo */}
      <TimeDistributionModal
        isOpen={showDistributionModal}
        onClose={() => setShowDistributionModal(false)}
        onConfirm={handleConfirmDistribution}
        session={session}
        loading={loading}
        targetDocument={targetDocument}
      />
    </>
  );
};

export default StopTimerModal;
